import { DraggableWindowHeader } from "@hoylu/client-common";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import type { RootState } from "typesafe-actions";
import { transferService } from "../../../state/config/config.selector";
import { authorizationHeader } from "../../../state/user/user.selector";
import {
  cancelDashboardOption,
  queueEditWorkspace,
} from "../../../state/workspaces/workspaces.actions";
import { Localized } from "../../../strings";
import { Modal } from "../Modal";
import { Progress } from "./Progress";
import { SelectFile } from "./SelectFile";
import Styles from "./UploadDialog.module.css";
import { UploadDialogError } from "./UploadDialogError";
import { error, startUpload, UploadActionType } from "./uploadTypes";
import { uploadWorkspace } from "./uploadWorkspace";
import { UploadStage, useUploadReducer } from "./useUploadReducer";

const mapStateToProps = (state: RootState) => ({
  transferServiceUrl: transferService(state),
  authHeader: authorizationHeader(state),
  // TODO: This prop doesn't appear to be used
  fetchApi: window.fetch,
});

const mapDispatchToProps = {
  openWorkspace: queueEditWorkspace,
  cancelDashboardOption,
};

type UploadDialogProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

export const UploadDialog: React.FC<UploadDialogProps> = ({
  openWorkspace,
  cancelDashboardOption,
  transferServiceUrl,
  authHeader,
}) => {
  const strings = Localized.object("UPLOAD_DIALOG");
  const tClose = Localized.object("CLOSE_BUTTON");

  const [state, dispatch] = useUploadReducer();

  const abortControllerRef = useRef<AbortController | null>(null);
  const isActive = useRef(true);
  useEffect(() => {
    return () => {
      isActive.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const onSubmit = async (file: File) => {
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch(startUpload(file.name));

    const actions = uploadWorkspace(
      file,
      transferServiceUrl,
      authHeader.Authorization,
      abortController.signal,
    );

    try {
      for await (const action of actions) {
        if (!isActive.current) return;
        switch (action.type) {
          case UploadActionType.ERROR:
            abortControllerRef.current = null;
            console.error(action.payload.message);
            dispatch(action);
            break;
          case UploadActionType.PROGRESS:
            dispatch(action);
            break;
          case UploadActionType.OPEN_WORKSPACE:
            abortControllerRef.current = null;
            openWorkspace({ workspaceId: action.payload.workspaceId });
            break;
          default:
            break;
        }
      }
    } catch (e) {
      if (isActive.current) {
        dispatch(error(String(e)));
      }
      console.log(e);
    }
  };

  return (
    <Modal>
      <div
        className={Styles.uploadDialog}
        onDragOver={(e: React.DragEvent<HTMLDivElement>) => {
          if (state.stage !== UploadStage.SELECT) return;
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e: React.DragEvent<HTMLDivElement>) => {
          if (state.stage !== UploadStage.SELECT) return;
          e.preventDefault();
          e.stopPropagation();

          if (
            e.dataTransfer.files.length !== 1 ||
            e.dataTransfer.files[0].type !== "application/zip"
          ) {
            console.warn("invalid upload");
            return;
          }
          onSubmit(e.dataTransfer.files[0]);
        }}
      >
        <DraggableWindowHeader
          close={tClose.CLOSE}
          onClose={cancelDashboardOption}
          title={strings.UPLOADING_WORKSPACE}
        />
        {state.stage === UploadStage.SELECT && (
          <SelectFile onSubmit={onSubmit} strings={strings} />
        )}
        {state.stage === UploadStage.PROGRESS && <Progress {...state} />}
        {state.stage === UploadStage.ERROR && (
          <UploadDialogError message={state.message} strings={strings} />
        )}
      </div>
    </Modal>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(UploadDialog);
